'use client';

import React, { useState, useEffect } from 'react';
import { fetchVendorPivotPointAnalysis } from '../../apiHelper';
import { Gavel, TrendingUp, Users, ChevronRight, Award, ShieldCheck } from 'lucide-react';

const formatCurrency = (val) => {
    if (!val) return '₦0';
    if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
    return `₦${val.toLocaleString()}`;
};

const VendorPivotPointDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const result = await fetchVendorPivotPointAnalysis({
                    lookback_days: 90,
                    pivot_threshold: 8.0
                });
                setData(result);
            } catch (err) {
                console.error("Error fetching pivot data:", err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    if (loading) return <div className="animate-pulse h-96 bg-gray-50 rounded-3xl" />;

    const vendors = data?.vendors || [];
    const summary = data?.summary || {};

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Gavel className="text-orange-600" size={20} />
                        <h3 className="text-xl font-bold text-gray-900">Negotiation Leverage</h3>
                    </div>
                    <p className="text-xs text-gray-500">Vendor Pivot Point Analysis</p>
                </div>
                <div className="text-right">
                    <span className="text-[10px] font-black text-orange-600 bg-orange-50 px-2 py-1 rounded-lg uppercase">
                        {data?.pivot_reached_count} Pivots Reached
                    </span>
                </div>
            </div>

            {/* Pivot List */}
            <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2 custom-scrollbar">
                {vendors.map((vendor, i) => (
                    <div
                        key={i}
                        className="p-5 border border-gray-100 rounded-3xl bg-white hover:border-orange-200 transition-all group"
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-white shadow-sm ${vendor.pivot_score > 80 ? 'bg-orange-500' : 'bg-gray-400'
                                    }`}>
                                    {vendor.vendor_name.charAt(0)}
                                </div>
                                <div>
                                    <p className="font-bold text-gray-900 text-sm leading-tight group-hover:text-orange-600 transition-colors">
                                        {vendor.vendor_name}
                                    </p>
                                    <p className="text-[10px] text-gray-400 font-medium uppercase mt-1">
                                        {vendor.status}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-xs font-black text-gray-800">{vendor.pivot_score}%</p>
                                <p className="text-[10px] text-gray-400 uppercase">Pivot Score</p>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full h-1.5 bg-gray-100 rounded-full mb-4 overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-1000 ${vendor.pivot_score > 80 ? 'bg-orange-500' : 'bg-blue-500'
                                    }`}
                                style={{ width: `${vendor.pivot_score}%` }}
                            />
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="text-center p-2 rounded-2xl bg-gray-50/50">
                                <p className="text-[9px] text-gray-400 uppercase font-bold">Volume</p>
                                <p className="text-xs font-bold text-gray-700">{formatCurrency(vendor.annual_receipt_value)}</p>
                            </div>
                            <div className="text-center p-2 rounded-2xl bg-gray-50/50 border border-transparent group-hover:border-orange-100 group-hover:bg-white">
                                <p className="text-[9px] text-gray-400 uppercase font-bold">Our Share</p>
                                <p className="text-xs font-bold text-orange-600">{vendor.estimated_share_of_vendor_output}%</p>
                            </div>
                            <div className="text-center p-2 rounded-2xl bg-gray-50/50">
                                <p className="text-[9px] text-gray-400 uppercase font-bold">Credit</p>
                                <p className="text-xs font-bold text-gray-700">{vendor.payment_terms_days}d</p>
                            </div>
                        </div>

                        {/* Recommendation */}
                        {vendor.pivot_score > 80 && (
                            <div className="mt-4 flex items-center gap-2 text-[10px] font-medium text-emerald-600 bg-emerald-50 py-2 px-3 rounded-xl">
                                <Award size={14} />
                                {vendor.recommendation}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Global Recommendation Footer */}
            <div className="mt-auto pt-6">
                <div className="bg-gray-900 rounded-2xl p-5 text-white shadow-xl">
                    <div className="flex gap-4">
                        <div className="p-2 bg-orange-500 rounded-xl h-fit">
                            <ShieldCheck size={20} />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-orange-400 uppercase tracking-widest mb-1">Strategy Focus</p>
                            <p className="text-xs leading-relaxed opacity-90">
                                {data?.recommendation.replace(/\*\*/g, '')} Dictate better credit terms to improve your Cash-to-Cash cycle.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VendorPivotPointDashboard;